import { FormattedNumber, FormattedRelativeTime, useIntl } from 'react-intl';
import { Typography } from '@evaneos/b2b-ui';
import { ReactNode } from 'react';

import {
    InstalmentPaidEnrichedData,
    Notification,
    NotificationType,
} from '../../../../resources/notifications/types';
import { bestFitUnitDateDifference } from '../../../../shared/utilities/duration';
import { AllTranslationKeys } from '../../../../shared/intl/types';

export const NotificationItemContent = ({ notification }: { notification: Notification }) => {
    const { formatMessage } = useIntl();
    const { value, unit } = bestFitUnitDateDifference(notification.occurredOn);

    const getNotificationMessage = (
        id: AllTranslationKeys,
        name: string,
        additionalParams?: Record<string, unknown>,
    ) =>
        formatMessage(
            { id },
            {
                name: (
                    <Typography
                        color="inherit"
                        component="strong"
                        fontWeight={700}
                        display="inline"
                    >
                        {name}
                    </Typography>
                ),
                ...additionalParams,
            },
        );

    let notificationMessage: ReactNode = null;
    switch (notification.type satisfies NotificationType) {
        case 'proposal.validated':
            notificationMessage = getNotificationMessage(
                'homepage.notifications.proposal_validated',
                notification.requesterName,
            );
            break;
        case 'instalment.paid': {
            const { amount, currency } = notification.enrichedData as InstalmentPaidEnrichedData;
            const amountInMainUnit = Number.parseInt(amount, 10) / 100;

            notificationMessage = getNotificationMessage(
                'homepage.notifications.payment_received',
                notification.requesterName,
                {
                    amount: (
                        <FormattedNumber
                            value={amountInMainUnit}
                            style="currency"
                            currency={currency}
                            minimumFractionDigits={2}
                            maximumFractionDigits={2}
                        />
                    ),
                },
            );
            break;
        }
        case 'proposal.plan_accommodations_selected':
            notificationMessage = getNotificationMessage(
                'homepage.notifications.alternatives_picked',
                notification.requesterName,
            );
            break;
        case 'dossier.automatically_set_lost':
            notificationMessage = getNotificationMessage(
                'homepage.notifications.automatically_set_lost',
                notification.requesterName,
            );
            break;
        case 'dossier.at1_auto_sent':
            notificationMessage = getNotificationMessage(
                'homepage.notifications.at1_auto_sent',
                notification.requesterName,
            );
            break;
        default:
            break;
    }

    if (!notificationMessage) {
        return null;
    }

    return (
        <Typography
            display="flex"
            gap={0.5}
            color={notification.read ? 'text.secondary' : 'text.primary'}
            flexDirection="column"
            alignItems="start"
        >
            <span>{notificationMessage}</span>
            <Typography component="span" display="flex" alignItems="center" gap={0.5}>
                <Typography component="span" variant="body2" color="text.secondary">
                    <FormattedRelativeTime value={value} unit={unit} style="long" />
                </Typography>
            </Typography>
        </Typography>
    );
};
